"use client";

/**
 * Brand website — ROOMS tab (accommodation, 2026-09).
 *
 * Pick dates, see what is free and what it costs, hold a room with your name
 * and phone. The property's house rules (check-in time, deposit, hold window)
 * come from its settings; availability is the same public query the front
 * desk uses, so a room shown free here is free.
 */

import { useMemo, useState } from "react";
import Link from "next/link";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { BedDouble, CalendarDays, Minus, Plus, Users, Wifi } from "lucide-react";
import { BookRoomDialog } from "@/components/accommodation/BookRoomDialog";
import {
  addDays,
  dayIntFromISO,
  daysBetween,
  formatDayIntLong,
  isoFromDayInt,
  todayDayInt,
} from "@/lib/config/accommodation";

interface BrandRoomsSectionProps {
  brandId: string;
  storeId?: string;
  brandName: string;
  currency?: string;
}

const NAVY = "#0f172a";
const SLATE = "#64748b";
const money = (n: number, cur = "UGX") => `${cur} ${Math.round(n || 0).toLocaleString("en-US")}`;

function Stepper({ value, onChange, min, max }: { value: number; onChange: (v: number) => void; min: number; max: number }) {
  return (
    <div className="inline-flex items-center rounded-full border" style={{ borderColor: "#e2e8f0" }}>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} className="p-2 hover:bg-slate-100 rounded-l-full" aria-label="Fewer">
        <Minus className="w-4 h-4" style={{ color: SLATE }} />
      </button>
      <span className="w-8 text-center text-sm font-black tabular-nums" style={{ color: NAVY }}>{value}</span>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} className="p-2 hover:bg-slate-100 rounded-r-full" aria-label="More">
        <Plus className="w-4 h-4" style={{ color: SLATE }} />
      </button>
    </div>
  );
}

export default function BrandRoomsSection({ brandId, storeId, brandName, currency = "UGX" }: BrandRoomsSectionProps) {
  const today = todayDayInt();
  const [checkIn, setCheckIn] = useState(isoFromDayInt(today));
  const [nights, setNights] = useState(1);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [booking, setBooking] = useState<any | null>(null);

  const checkInDay = useMemo(() => {
    try {
      return checkIn ? dayIntFromISO(checkIn) : today;
    } catch {
      return today;
    }
  }, [checkIn, today]);
  const checkOutDay = addDays(checkInDay, Math.max(1, nights));
  const valid = checkInDay >= today;

  const availability = useQuery(
    api.accommodation.availability.getAvailability,
    brandId && valid
      ? {
          brandId: brandId as Id<"brands">,
          storeId: storeId ? (storeId as Id<"stores">) : undefined,
          rateMode: "nightly",
          checkInDay,
          checkOutDay,
          adults,
        }
      : "skip",
  );

  const rooms = availability?.roomTypes ?? [];

  return (
    <section id="rooms" className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
      <div className="flex items-center gap-2.5 mb-6">
        <BedDouble className="w-5 h-5" style={{ color: NAVY }} />
        <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wide" style={{ color: NAVY }}>Rooms</h2>
        {availability && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#f1f5f9", color: SLATE }}>
            {rooms.filter((r) => r.available).length} available
          </span>
        )}
      </div>

      {/* Search bar */}
      <div className="rounded-2xl bg-white p-4 sm:p-5 mb-6 grid grid-cols-2 lg:grid-cols-[1fr_auto_auto_auto] gap-4 items-end" style={{ border: "1px solid #e2e8f0" }}>
        <label className="block col-span-2 lg:col-span-1">
          <span className="block text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: SLATE }}>Check-in</span>
          <input
            type="date"
            min={isoFromDayInt(today)}
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
            className="w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none focus:ring-2"
            style={{ borderColor: "#e2e8f0", color: NAVY }}
          />
          {availability && (
            <span className="block text-[11px] mt-1" style={{ color: SLATE }}>
              Check-out {formatDayIntLong(checkOutDay)} · from {availability.settings.checkInTime}, out by {availability.settings.checkOutTime}
            </span>
          )}
        </label>
        <div>
          <span className="block text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: SLATE }}>Nights</span>
          <Stepper value={nights} onChange={setNights} min={1} max={30} />
        </div>
        <div>
          <span className="block text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: SLATE }}>Adults</span>
          <Stepper value={adults} onChange={setAdults} min={1} max={12} />
        </div>
        <div>
          <span className="block text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: SLATE }}>Children</span>
          <Stepper value={children} onChange={setChildren} min={0} max={8} />
        </div>
      </div>

      {!valid ? (
        <p className="text-sm py-10 text-center" style={{ color: SLATE }}>Pick today or a later date.</p>
      ) : availability === undefined ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-2xl h-80 animate-pulse" style={{ backgroundColor: "#f1f5f9" }} />
          ))}
        </div>
      ) : rooms.length === 0 ? (
        <p className="text-sm py-10 text-center" style={{ color: SLATE }}>No rooms are listed yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {rooms.map((r) => {
            const amenities = (r.stay?.amenities || []) as string[];
            return (
              <div key={String(r._id)} className="group rounded-2xl overflow-hidden bg-white transition-shadow hover:shadow-lg flex flex-col" style={{ border: "1px solid #e2e8f0", opacity: r.available ? 1 : 0.85 }}>
                <Link href={r.slug ? `/rooms/${r.slug}` : "#rooms"} className="relative block h-48 overflow-hidden" style={{ backgroundColor: "#f1f5f9" }} aria-label={`${r.name} — details and photos`}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={r.imageUrl || "/images/placeholder-product.jpg"} alt={r.name} className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
                  <span
                    className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-bold"
                    style={{ backgroundColor: r.available ? "rgba(34,197,94,0.92)" : "rgba(15,23,42,0.8)", color: "#fff" }}
                  >
                    {r.available ? (r.unitsFree === 1 ? "Last room" : `${r.unitsFree} rooms free`) : r.unitsFree === 0 ? "Fully booked" : "Not available"}
                  </span>
                  {r.featured && (
                    <span className="absolute right-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-bold text-white" style={{ backgroundColor: "rgba(147,51,234,0.92)" }}>Popular</span>
                  )}
                </Link>
                <div className="p-4 sm:p-5 flex flex-col flex-1">
                  <h3 className="text-base font-bold leading-snug" style={{ color: NAVY }}>
                    {r.slug ? <Link href={`/rooms/${r.slug}`} className="hover:underline">{r.name}</Link> : r.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] font-semibold" style={{ color: SLATE }}>
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> Sleeps {r.stay?.maxAdults ?? 2}{r.stay?.maxChildren ? ` + ${r.stay.maxChildren} child${r.stay.maxChildren === 1 ? "" : "ren"}` : ""}</span>
                    {r.stay?.bedType && <span className="inline-flex items-center gap-1"><BedDouble className="w-3 h-3" /> {r.stay.bedType}</span>}
                    {r.stay?.sizeSqm ? <span>{r.stay.sizeSqm} m²</span> : null}
                  </div>
                  {r.description && <p className="text-xs mt-2 line-clamp-2" style={{ color: SLATE }}>{r.description}</p>}
                  {amenities.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {amenities.slice(0, 5).map((a) => (
                        <span key={a} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ backgroundColor: "#f1f5f9", color: SLATE }}>
                          {a === "Wi-Fi" && <Wifi className="w-2.5 h-2.5" />}{a}
                        </span>
                      ))}
                      {amenities.length > 5 && <span className="text-[10px] font-semibold px-1 py-0.5" style={{ color: SLATE }}>+{amenities.length - 5} more</span>}
                    </div>
                  )}
                  <div className="flex items-end justify-between gap-3 mt-auto pt-4">
                    <div>
                      <span className="block text-base sm:text-lg font-black leading-none" style={{ color: NAVY }}>{money(r.price, currency)}</span>
                      <span className="block text-[11px] mt-0.5" style={{ color: SLATE }}>
                        per night{r.stay?.weekendRate && r.stay.weekendRate !== r.price ? ` · Fri–Sat ${money(r.stay.weekendRate, currency)}` : ""}
                      </span>
                      {r.available && (
                        <span className="block text-[11px] font-semibold mt-1" style={{ color: NAVY }}>
                          {money(r.total, currency)} for {daysBetween(checkInDay, checkOutDay)} night{daysBetween(checkInDay, checkOutDay) === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={!r.available}
                      onClick={() => setBooking(r)}
                      className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ backgroundColor: NAVY }}
                      onMouseEnter={(e) => { if (r.available) (e.currentTarget as HTMLElement).style.backgroundColor = "#334155"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = NAVY; }}
                    >
                      <CalendarDays className="w-3.5 h-3.5" />
                      {r.available ? "Book" : "Unavailable"}
                    </button>
                  </div>
                  {!r.available && r.unavailableReason && (
                    <p className="text-[11px] mt-2" style={{ color: SLATE }}>{r.unavailableReason}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {booking && availability && (
        <BookRoomDialog
          brandId={brandId}
          storeId={storeId}
          brandName={brandName}
          currency={currency}
          room={booking}
          checkInDay={checkInDay}
          checkOutDay={checkOutDay}
          adults={adults}
          children={children}
          settings={availability.settings}
          onClose={() => setBooking(null)}
        />
      )}
    </section>
  );
}
